import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { trackCampaign } from "@/lib/campaignTrack";
import { isGated, markSeen } from "@/lib/campaignFrequency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shows at most one popup banner per page load: the first eligible popup
 * campaign (passing its how-often gate), after its configured delay. Clicking
 * the banner opens the Share-a-Fit form.
 */
const CampaignPopup = ({ campaigns, onShareClick }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Pick the first popup campaign that isn't currently gated out.
  const popups = (campaigns || []).filter((c) => c.placement === "popup");
  const chosen = popups.find((c) => !isGated(c)) || null;

  useBodyScrollLock(visible);

  useEffect(() => {
    if (!chosen || dismissed) return;
    const delay = Math.max(0, Number(chosen.delay_seconds || 0)) * 1000;
    const t = setTimeout(() => {
      setVisible(true);
      markSeen(chosen);
      trackCampaign("campaign_impression", chosen, { placement: "popup" });
    }, delay);
    return () => clearTimeout(t);
    // Re-arm only when the chosen campaign changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen?.id, dismissed]);

  if (!chosen || !visible || dismissed) return null;

  const close = () => {
    trackCampaign("campaign_dismiss", chosen, { placement: "popup" });
    setVisible(false);
    setDismissed(true);
  };

  const handleBannerClick = () => {
    trackCampaign("campaign_click", chosen, { placement: "popup" });
    setVisible(false);
    setDismissed(true);
    onShareClick?.();
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center"
      onClick={close}
      data-testid="campaign-popup-overlay"
    >
      <div
        className="relative w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute -top-3 -right-3 z-10 bg-white text-[#2D302A] rounded-full p-2 shadow-lg hover:bg-[#F0EFEB] transition-colors"
          aria-label="Close"
          data-testid="campaign-popup-close"
        >
          <X size={20} weight="bold" />
        </button>
        <button
          type="button"
          onClick={handleBannerClick}
          className="block w-full rounded-lg overflow-hidden shadow-2xl focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Share your fit"
          data-testid="campaign-popup-banner"
        >
          <img
            src={`${BACKEND_URL}${chosen.image_url}`}
            alt={chosen.title || "Featured"}
            className="w-full h-auto block"
          />
        </button>
      </div>
    </div>
  );
};

export default CampaignPopup;
